import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import {
  Sparkles,
  CheckCircle2,
  Calendar,
  User,
  Users,
  ArrowRight,
  ArrowLeft,
  RotateCcw,
  Trash2,
  Plus,
  QrCode,
  Copy,
  Check,
  ShieldCheck,
  AlertCircle,
  Lock,
  Smartphone,
} from 'lucide-react';
import HomamSubNavbar from '../../components/HomamSubNavbar';
import HomamFooter from '../../components/HomamFooter';
import { MOCK_NAKSHATRAS, HOMAM_SEVA_PLANS } from '../../data/homamData';
import { API_ENDPOINTS } from '../../config/api';
import { getHomamPath } from '../../utils/domain';
import templeQrImg from '../../assets/qr-code.jpeg';
import QRCode from 'qrcode';
import '../../styles/HomamPortal.css';
import '../../styles/HomamBookingFlow.css';

const RELATIONSHIP_OPTIONS = [
  'Wife',
  'Husband',
  'Son',
  'Daughter',
  'Father',
  'Mother',
  'Brother',
  'Sister',
  'Father-in-law',
  'Mother-in-law',
  'Grandfather',
  'Grandmother',
  'Other Relative'
];

export function HomamBookingPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.title = "Seva Plan & Payment | 27 Nakshatra Maha Homam";
  }, []);

  // Retrieve passed devotee and star details from location state, search query, or fallback
  const queryParams = new URLSearchParams(location.search);
  const queryStarId = queryParams.get('star');
  const querySlug = queryParams.get('slug');
  const passedBooking = location.state?.booking;
  const passedNakshatra = location.state?.nakshatra;

  // If accessed directly without filling the devotee registration form first, redirect to the registration page
  useEffect(() => {
    if (!passedBooking && !queryStarId && !querySlug) {
      navigate('/homam-registration', { replace: true });
    }
  }, [passedBooking, queryStarId, querySlug, navigate]);

  const selectedNakshatra =
    passedNakshatra ||
    (queryStarId ? MOCK_NAKSHATRAS.find((n) => n.id === Number(queryStarId)) : null) ||
    (querySlug ? MOCK_NAKSHATRAS.find((n) => n.slug === querySlug) : null) ||
    (passedBooking?.nakshatraId
      ? MOCK_NAKSHATRAS.find((n) => n.id === Number(passedBooking.nakshatraId))
      : MOCK_NAKSHATRAS[0]) ||
    MOCK_NAKSHATRAS[0];

  const [bookingRecord, setBookingRecord] = useState(
    passedBooking || {
      name: 'Registered Devotee',
      mobile: '9876543210',
      city: 'Temple Town',
      bookingId: `SGSMH${Date.now().toString().slice(-6)}`,
      _id: 'temp_booking_id',
    }
  );

  // Steps: 'plan_selection' | 'family_members' | 'payment_qr' | 'confirmed'
  const [bookingStep, setBookingStep] = useState('plan_selection');
  const [selectedPlan, setSelectedPlan] = useState(HOMAM_SEVA_PLANS[1]); // Default to Family ₹5,001
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Payment State: UTR Number & Dynamic QR
  const [utrNumber, setUtrNumber] = useState('');
  const [utrError, setUtrError] = useState(null);
  const [copiedUpi, setCopiedUpi] = useState(false);

  const templeUpiId = "gnanasaiguruji@okhdfcbank";
  const templePayee = "Sri Gnana Sai Baba Mandir";

  // Dynamic UPI Payment Deep Link String
  const bookingCode = bookingRecord?.bookingId || `SGSMH${Date.now().toString().slice(-6)}`;
  const upiTransactionNote = `Homam Seva ${bookingCode} ${selectedPlan.name}`;
  const upiPaymentUri = `upi://pay?pa=${templeUpiId}&pn=${encodeURIComponent(templePayee)}&am=${selectedPlan.price}&cu=INR&tn=${encodeURIComponent(upiTransactionNote)}`;

  // Dynamic QR Code Data URL with embedded amount for instant scan autofill
  const [dynamicQrDataUrl, setDynamicQrDataUrl] = useState('');

  useEffect(() => {
    const note = `Homam Seva ${bookingCode} ${selectedPlan.name}`;
    const uri = `upi://pay?pa=${templeUpiId}&pn=${encodeURIComponent(templePayee)}&am=${selectedPlan.price}&cu=INR&tn=${encodeURIComponent(note)}`;

    QRCode.toDataURL(uri, {
      width: 280,
      margin: 1,
      color: {
        dark: '#111827',
        light: '#ffffff',
      },
      errorCorrectionLevel: 'H',
    })
      .then((url) => setDynamicQrDataUrl(url))
      .catch((err) => {
        console.error('Dynamic QR generation error:', err);
      });
  }, [selectedPlan.price, selectedPlan.name, bookingCode, templeUpiId, templePayee]);

  // Family Members State
  const [familyMembers, setFamilyMembers] = useState([
    {
      name: passedBooking?.name || 'Primary Devotee',
      relationship: 'Self / Kartha',
      nakshatra: selectedNakshatra.nameEn,
      isPrimary: true,
    },
  ]);

  // Keep primary devotee name synchronized
  useEffect(() => {
    if (bookingRecord?.name) {
      setFamilyMembers((prev) => {
        if (!prev || prev.length === 0) {
          return [
            {
              name: bookingRecord.name,
              relationship: 'Self / Kartha',
              nakshatra: selectedNakshatra.nameEn,
              isPrimary: true,
            },
          ];
        }
        const updated = [...prev];
        updated[0] = {
          ...updated[0],
          name: bookingRecord.name,
          nakshatra: updated[0]?.nakshatra || selectedNakshatra.nameEn,
          isPrimary: true,
        };
        return updated;
      });
    }
  }, [bookingRecord?.name, selectedNakshatra.nameEn]);

  // 1-Click Copy UPI ID
  const handleCopyUpi = () => {
    navigator.clipboard.writeText(templeUpiId);
    setCopiedUpi(true);
    setTimeout(() => setCopiedUpi(false), 2200);
  };

  // Add Family Member
  const handleAddMember = () => {
    const maxAllowed = selectedPlan?.membersAllowed || 6;
    if (familyMembers.length >= maxAllowed) return;
    const defaultRelations = ['Wife', 'Son', 'Daughter', 'Mother', 'Father', 'Brother', 'Sister', 'Grandparent'];
    const nextRel = defaultRelations[familyMembers.length - 1] || 'Family Member';
    setFamilyMembers((prev) => [
      ...prev,
      {
        name: '',
        relationship: nextRel,
        nakshatra: selectedNakshatra.nameEn,
        isPrimary: false,
      },
    ]);
  };

  // Remove Family Member (index > 0)
  const handleRemoveMember = (idx) => {
    if (idx === 0) return;
    setFamilyMembers((prev) => prev.filter((_, i) => i !== idx));
  };

  // Update Member Field
  const handleMemberChange = (idx, field, value) => {
    setFamilyMembers((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  // Step 1: Proceed from Plan Selection
  const handlePlanProceed = () => {
    setErrorMessage(null);
    if (selectedPlan.id === 'individual') {
      // Single devotee plan
      setFamilyMembers([
        {
          name: bookingRecord?.name || 'Primary Devotee',
          relationship: 'Self / Kartha',
          nakshatra: selectedNakshatra.nameEn,
          isPrimary: true,
        },
      ]);
      setBookingStep('payment_qr');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Family plan (up to 4 or 6 members)
      if (familyMembers.length === 1) {
        setFamilyMembers([
          {
            name: bookingRecord?.name || 'Primary Devotee',
            relationship: 'Self / Kartha',
            nakshatra: selectedNakshatra.nameEn,
            isPrimary: true,
          },
          {
            name: '',
            relationship: 'Wife',
            nakshatra: selectedNakshatra.nameEn,
            isPrimary: false,
          },
        ]);
      }
      setBookingStep('family_members');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Step 2: Validate and Proceed from Family Members to Payment & UTR
  const handleFamilyMembersProceed = () => {
    setErrorMessage(null);
    if (!familyMembers[0]?.name?.trim()) {
      setErrorMessage('Please enter the primary devotee name.');
      return;
    }
    const cleaned = familyMembers.filter((m, idx) => idx === 0 || m.name.trim() !== '');
    setFamilyMembers(cleaned);
    setBookingStep('payment_qr');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Strict Validation for 12-Digit UTR
  const validateUtr = (val) => {
    const clean = (val || '').trim().replace(/\s+/g, '').toUpperCase();
    if (!clean) {
      return { isValid: false, error: 'Please enter your 12-digit UPI Transaction ID / UTR Number to submit registration.' };
    }
    if (clean.length < 12) {
      return { isValid: false, error: `UTR number must be at least 12 digits (currently ${clean.length} digits entered).` };
    }
    if (/^(\w)\1+$/.test(clean)) {
      return { isValid: false, error: 'Please enter a genuine transaction UTR number from your payment receipt.' };
    }
    if (!/^[0-9A-Z]{12,22}$/.test(clean)) {
      return { isValid: false, error: 'UTR number should only contain digits and uppercase letters (no special characters).' };
    }
    return { isValid: true, clean };
  };

  const handleUtrInputChange = (e) => {
    const rawVal = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    setUtrNumber(rawVal);
    if (utrError) {
      const check = validateUtr(rawVal);
      if (check.isValid) setUtrError(null);
    }
  };

  // Step 3: Submit Registration with UTR Number
  const handleUtrPaymentSubmit = async (e) => {
    if (e) e.preventDefault();
    setErrorMessage(null);
    setUtrError(null);

    // Validate UTR strictly
    const validation = validateUtr(utrNumber);
    if (!validation.isValid) {
      setUtrError(validation.error);
      const utrEl = document.getElementById('homam-utr-input');
      if (utrEl) {
        utrEl.focus();
        utrEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const cleanUtr = validation.clean;

    try {
      setIsSubmitting(true);
      const isTemporary =
        !bookingRecord?._id ||
        bookingRecord._id === 'temp_booking_id' ||
        bookingRecord._id === 'pending_checkout' ||
        bookingRecord._id === 'temp_id' ||
        bookingRecord._id.startsWith('temp_');

      const cleanedMembers = familyMembers.filter((m) => m.name && m.name.trim() !== '');

      let activeUser = null;
      try {
        const stored = localStorage.getItem('user');
        if (stored) activeUser = JSON.parse(stored);
      } catch (_) {
        activeUser = null;
      }

      if (isTemporary) {
        const API_URL = API_ENDPOINTS.HOMAM_REGISTER || 'http://localhost:5000/api/homam/register';
        const res = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: bookingRecord?.name || activeUser?.fullName || 'Devotee',
            mobile: bookingRecord?.mobile || activeUser?.mobile || '9876543210',
            whatsappNumber: bookingRecord?.whatsappNumber || bookingRecord?.mobile || activeUser?.mobile || '9876543210',
            email: bookingRecord?.email || activeUser?.email || '',
            userId: activeUser?._id || bookingRecord?.userId || null,
            city: bookingRecord?.city || activeUser?.location || 'Mandir Devotee',
            gothram: bookingRecord?.gothram || '',
            rasi: bookingRecord?.rasi || '',
            message: bookingRecord?.message || '',
            nakshatraId: Number(selectedNakshatra.id),
            nakshatraNameEn: selectedNakshatra.nameEn,
            nakshatraNameTa: selectedNakshatra.nameTa,
            homamDate: selectedNakshatra.assignedDate,
            preferredDay: `Day ${selectedNakshatra.orderIndex} • ${selectedNakshatra.assignedDate}`,
            participantsCount: cleanedMembers.length,
            planId: selectedPlan.id,
            planName: selectedPlan.name,
            planAmount: selectedPlan.price,
            membersAllowed: selectedPlan.membersAllowed,
            utrNumber: cleanUtr,
            familyMembers: cleanedMembers,
            source: 'Website Booking Flow with UPI QR',
          }),
        });

        const resJson = await res.json();
        if (!res.ok || !resJson.success) {
          throw new Error(resJson.message || 'Failed to submit registration details.');
        }

        setBookingRecord(resJson.booking);
        setBookingStep('confirmed');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const targetId = bookingRecord._id || bookingRecord.bookingId;
        const API_URL = API_ENDPOINTS.HOMAM_PAYMENT
          ? API_ENDPOINTS.HOMAM_PAYMENT(targetId)
          : `http://localhost:5000/api/homam/bookings/${targetId}/payment`;

        const res = await fetch(API_URL, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            utrNumber: cleanUtr,
            planId: selectedPlan.id,
            planName: selectedPlan.name,
            planAmount: selectedPlan.price,
            membersAllowed: selectedPlan.membersAllowed,
            familyMembers: cleanedMembers,
          }),
        });

        const resJson = await res.json();
        if (!res.ok || !resJson.success) {
          throw new Error(resJson.message || 'Failed to update registration details.');
        }

        setBookingRecord(resJson.booking);
        setBookingStep('confirmed');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Registration submission error:', err);
      setErrorMessage(err.message || 'Unable to connect to Mandir server. Please check your network and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookingBack = () => {
    if (bookingStep === 'family_members') {
      setBookingStep('plan_selection');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (bookingStep === 'payment_qr') {
      if (selectedPlan.id !== 'individual') {
        setBookingStep('family_members');
      } else {
        setBookingStep('plan_selection');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      if (window.history.length > 1) {
        navigate(-1);
      } else if (selectedNakshatra?.slug) {
        navigate(getHomamPath(`/nakshatras/${selectedNakshatra.slug}`));
      } else {
        navigate('/homam-registration');
      }
    }
  };

  const isUtrComplete = utrNumber.length >= 12;

  return (
    <div className="homam-portal-wrapper">
      <HomamSubNavbar />

      {/* Main Dedicated Content Section */}
      <section style={{ padding: '36px 16px 80px 16px', minHeight: '80vh', background: '#FAF7F2' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>

          {/* Mobile-Only Back Button Action Bar */}
          <div className="homam-booking-top-bar">
            <button
              type="button"
              className="homam-mobile-page-back-btn"
              onClick={handleBookingBack}
              aria-label="Back to previous page"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back</span>
            </button>
          </div>

          {/* Top Progress Stepper */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 10,
            marginBottom: 28,
            flexWrap: 'wrap',
          }}>
            {/* Step 1: Devotee Details */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: '#16a34a' }}>
              <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#dcfce7', color: '#16a34a', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>✓</span>
              <span>1. Devotee Details</span>
            </div>
            <span style={{ color: '#cbd5e1' }}>―</span>

            {/* Step 2: Choose Seva Plan */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 800, color: bookingStep === 'plan_selection' ? '#0f2d5c' : '#16a34a' }}>
              <span style={{ width: 22, height: 22, borderRadius: '50%', background: bookingStep === 'plan_selection' ? '#0f2d5c' : '#dcfce7', color: bookingStep === 'plan_selection' ? '#f5c542' : '#16a34a', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
                {bookingStep === 'plan_selection' ? '2' : '✓'}
              </span>
              <span>2. Seva Plan</span>
            </div>
            <span style={{ color: '#cbd5e1' }}>―</span>

            {/* Step 3: Family Details (Visible if Family Plan) */}
            {selectedPlan.id !== 'individual' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 800, color: bookingStep === 'family_members' ? '#0f2d5c' : ['payment_qr', 'confirmed'].includes(bookingStep) ? '#16a34a' : '#94a3b8' }}>
                  <span style={{ width: 22, height: 22, borderRadius: '50%', background: bookingStep === 'family_members' ? '#0f2d5c' : ['payment_qr', 'confirmed'].includes(bookingStep) ? '#dcfce7' : '#e2e8f0', color: bookingStep === 'family_members' ? '#f5c542' : ['payment_qr', 'confirmed'].includes(bookingStep) ? '#16a34a' : '#64748b', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
                    {['payment_qr', 'confirmed'].includes(bookingStep) ? '✓' : '3'}
                  </span>
                  <span>3. Family Details</span>
                </div>
                <span style={{ color: '#cbd5e1' }}>―</span>
              </>
            )}

            {/* Step 4: Payment & UTR */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 800, color: bookingStep === 'payment_qr' ? '#0f2d5c' : bookingStep === 'confirmed' ? '#16a34a' : '#94a3b8' }}>
              <span style={{ width: 22, height: 22, borderRadius: '50%', background: bookingStep === 'payment_qr' ? '#0f2d5c' : bookingStep === 'confirmed' ? '#dcfce7' : '#e2e8f0', color: bookingStep === 'payment_qr' ? '#f5c542' : bookingStep === 'confirmed' ? '#16a34a' : '#64748b', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
                {bookingStep === 'confirmed' ? '✓' : selectedPlan.id === 'individual' ? '3' : '4'}
              </span>
              <span>{selectedPlan.id === 'individual' ? '3' : '4'}. QR Payment & UTR</span>
            </div>
            <span style={{ color: '#cbd5e1' }}>―</span>

            {/* Step 5: Verification Receipt */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 800, color: bookingStep === 'confirmed' ? '#16a34a' : '#94a3b8' }}>
              <span style={{ width: 22, height: 22, borderRadius: '50%', background: bookingStep === 'confirmed' ? '#16a34a' : '#e2e8f0', color: bookingStep === 'confirmed' ? '#ffffff' : '#64748b', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
                {selectedPlan.id === 'individual' ? '4' : '5'}
              </span>
              <span>{selectedPlan.id === 'individual' ? '4' : '5'}. Verification</span>
            </div>
          </div>

          {/* Devotee & Star Top Banner */}
          <div className="homam-booked-banner">
            <div className="homam-booked-meta">
              <h4>YOUR HOMAM REGISTRATION</h4>
              <h3>{selectedNakshatra.nameEn} Nakshatra Homam</h3>
              <div className="homam-booked-chips">
                <span className="homam-booked-chip">
                  <Calendar className="w-3.5 h-3.5 text-amber-300" />
                  <span>Homam Date: {selectedNakshatra.assignedDate}</span>
                </span>
                <span className="homam-booked-chip">
                  <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                  <span>Day {selectedNakshatra.orderIndex} • {selectedNakshatra.nameTa}</span>
                </span>
                <span className="homam-booked-chip">
                  <User className="w-3.5 h-3.5 text-amber-300" />
                  <span>Devotee: {bookingRecord?.name}</span>
                </span>
              </div>
            </div>
          </div>

          {/* ================= STEP 1: CHOOSE SEVA PLAN ================= */}
          {bookingStep === 'plan_selection' && (
            <div style={{ animation: 'fadeInHomam 0.3s ease-out' }}>
              <div className="homam-step-title">
                <h3>Choose Your Sacred Seva Plan</h3>
                <p>Select your auspicious Homam participation level for {selectedNakshatra.nameEn} Nakshatra</p>
              </div>

              {/* 3-Column Plan Cards */}
              <div className="homam-plans-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 24 }}>
                {HOMAM_SEVA_PLANS.map((plan) => {
                  const isSelected = selectedPlan.id === plan.id;
                  return (
                    <div
                      key={plan.id}
                      onClick={() => setSelectedPlan(plan)}
                      className={`homam-plan-card ${isSelected ? 'selected' : ''}`}
                      style={{ padding: '28px 22px' }}
                    >
                      {plan.badge && (
                        <span className="homam-plan-badge-top">{plan.badge}</span>
                      )}

                      <div className="homam-plan-header">
                        <h4 style={{ fontSize: '1.35rem' }}>{plan.name}</h4>
                        <span className="homam-plan-ta" style={{ fontSize: 13 }}>{plan.nameTa}</span>
                        <div className="homam-plan-price" style={{ fontSize: '2.1rem' }}>
                          ₹{plan.price.toLocaleString('en-IN')}
                        </div>
                        <div className="homam-plan-members-pill" style={{ fontSize: 12, padding: '5px 12px' }}>
                          {plan.membersText}
                        </div>
                      </div>

                      <ul className="homam-plan-features" style={{ margin: '16px 0 24px 0' }}>
                        {plan.features.map((feat, idx) => (
                          <li key={idx} style={{ fontSize: 13, marginBottom: 10 }}>
                            <CheckCircle2 className="w-4 h-4 text-amber-600" />
                            <span>{feat}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="homam-plan-radio">
                        <button
                          type="button"
                          className="btn-select-plan"
                          style={{ padding: '12px 18px', fontSize: 13.5 }}
                        >
                          {isSelected ? '✓ Selected Plan' : 'Select This Plan'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="homam-flow-actions" style={{ marginTop: 32 }}>
                <button
                  type="button"
                  onClick={handlePlanProceed}
                  className="btn-homam-primary"
                  style={{ fontSize: 15, padding: '14px 32px' }}
                >
                  <span>
                    {selectedPlan.id === 'individual'
                      ? `Proceed to Payment & QR (₹${selectedPlan.price.toLocaleString('en-IN')})`
                      : `Add Family Details (Up to ${selectedPlan.membersAllowed || 6} Members) →`}
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ================= STEP 2: FAMILY MEMBERS SCREEN ================= */}
          {bookingStep === 'family_members' && (
            <div className="family-members-container">
              <div className="homam-step-title" style={{ marginBottom: 16 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fef3c7', color: '#92400e', padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 800, marginBottom: 8 }}>
                  <Users className="w-3.5 h-3.5" />
                  <span>{selectedPlan.name} • Allowed: Up to {selectedPlan.membersAllowed || 6} Members</span>
                </div>
                <h3>Family Members for Homam</h3>
                <p>Add up to {selectedPlan.membersAllowed || 6} family members for the sacred {selectedPlan.name} chanting</p>
              </div>

              {/* Progress Bar Header */}
              <div className="family-progress-wrap">
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Users className="w-4 h-4 text-amber-600" />
                  <span>{familyMembers.length} / {selectedPlan.membersAllowed || 6} Members Added</span>
                </span>
                <span style={{ color: '#64748b', fontSize: 12 }}>Max {selectedPlan.membersAllowed || 6} Members</span>
              </div>
              <div className="family-progress-bar-bg" style={{ marginBottom: 20 }}>
                <div className="family-progress-bar-fill" style={{ width: `${(familyMembers.length / (selectedPlan.membersAllowed || 6)) * 100}%` }} />
              </div>

              {/* Member Cards List */}
              {familyMembers.map((member, idx) => {
                const isPrimary = idx === 0;
                return (
                  <div key={idx} className={`family-member-card ${isPrimary ? 'family-primary-card' : ''}`}>
                    <div className="family-card-top">
                      <div className="family-card-title">
                        <User className="w-3.5 h-3.5" style={{ color: isPrimary ? '#b8860b' : '#64748b' }} />
                        <span>{isPrimary ? 'PRIMARY DEVOTEE' : `FAMILY MEMBER ${idx + 1}`}</span>
                      </div>

                      {isPrimary ? (
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', background: '#dcfce7', padding: '3px 8px', borderRadius: 12 }}>
                          ✓ Primary Registrant
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleRemoveMember(idx)}
                          className="family-remove-btn"
                          title="Remove this member"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    <div className="family-inputs-grid">
                      {/* Full Name */}
                      <div className="family-input-field">
                        <label>Full Name *</label>
                        <input
                          type="text"
                          placeholder={isPrimary ? 'Devotee Name' : 'Enter family member name'}
                          value={member.name}
                          onChange={(e) => handleMemberChange(idx, 'name', e.target.value)}
                          required
                        />
                      </div>

                      {/* Relationship */}
                      <div className="family-input-field">
                        <label>Relationship *</label>
                        {isPrimary ? (
                          <input
                            type="text"
                            value="Self / Kartha"
                            readOnly
                            style={{ background: '#f8fafc', color: '#64748b', cursor: 'default' }}
                          />
                        ) : (
                          <select
                            value={member.relationship}
                            onChange={(e) => handleMemberChange(idx, 'relationship', e.target.value)}
                          >
                            {RELATIONSHIP_OPTIONS.map((rel) => (
                              <option key={rel} value={rel}>{rel}</option>
                            ))}
                          </select>
                        )}
                      </div>

                      {/* Birth Nakshatra */}
                      <div className="family-input-field">
                        <label>Birth Nakshatra (Star)</label>
                        <select
                          value={member.nakshatra}
                          onChange={(e) => handleMemberChange(idx, 'nakshatra', e.target.value)}
                        >
                          {MOCK_NAKSHATRAS.map((n) => (
                            <option key={n.id} value={n.nameEn}>
                              {n.nameEn} ({n.nameTa})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Add Member Button */}
              <button
                type="button"
                onClick={handleAddMember}
                disabled={familyMembers.length >= (selectedPlan.membersAllowed || 6)}
                className="btn-add-member"
              >
                <Plus className="w-4 h-4 text-amber-600" />
                <span>
                  {familyMembers.length >= (selectedPlan.membersAllowed || 6)
                    ? `Maximum ${selectedPlan.membersAllowed || 6} of ${selectedPlan.membersAllowed || 6} Members Added`
                    : `+ Add Family Member (${(selectedPlan.membersAllowed || 6) - familyMembers.length} remaining)`}
                </span>
              </button>

              <div style={{ background: '#FAF7F2', borderRadius: 12, padding: '12px 16px', border: '1px solid #EADDC7', fontSize: 12, color: '#64748b', marginBottom: 24, lineHeight: 1.5 }}>
                💡 <strong>Temple Homam Note:</strong> All member names, relationships, and birth stars will be chanted aloud into the sacred sacrificial fire during the Purnahuti.
              </div>

              {errorMessage && (
                <div style={{ marginBottom: 16, padding: 12, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 12, fontSize: 13 }}>
                  ⚠️ {errorMessage}
                </div>
              )}

              {/* Action Buttons */}
              <div className="homam-flow-actions">
                <button
                  type="button"
                  onClick={() => setBookingStep('plan_selection')}
                  className="btn-homam-secondary"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to Plans</span>
                </button>

                <button
                  type="button"
                  onClick={handleFamilyMembersProceed}
                  className="btn-homam-primary"
                >
                  <span>Continue to Payment & QR</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ================= STEP 3: DYNAMIC AUTOFILLED QR PAYMENT & UTR INPUT ================= */}
          {bookingStep === 'payment_qr' && (
            <div className="homam-payment-qr-container">
              <div className="homam-step-title" style={{ marginBottom: 18 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fef3c7', color: '#92400e', padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 800, marginBottom: 8 }}>
                  <QrCode className="w-3.5 h-3.5" />
                  <span>Scan to Pay • UPI QR Code</span>
                </div>
                <h3>Complete Seva Dakshina via UPI</h3>
                <p>Scan the QR code with your payment app. The Seva Dakshina amount is pre-filled automatically.</p>
              </div>

              <div className="homam-payment-card">

                {/* Compact Order Review Summary */}
                <div style={{ background: '#FAF7F2', border: '1px solid #EADDC7', borderRadius: 14, padding: '14px 18px', marginBottom: 20, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #EADDC7', paddingBottom: 8, marginBottom: 8 }}>
                    <span style={{ color: '#64748b' }}>Honored Nakshatra:</span>
                    <strong style={{ color: '#0f2d5c' }}>{selectedNakshatra.nameEn} ({selectedNakshatra.nameTa}) • Day {selectedNakshatra.orderIndex}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #EADDC7', paddingBottom: 8, marginBottom: 8 }}>
                    <span style={{ color: '#64748b' }}>Homam Date:</span>
                    <strong style={{ color: '#0f2d5c' }}>{selectedNakshatra.assignedDate}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #EADDC7', paddingBottom: 8, marginBottom: 8 }}>
                    <span style={{ color: '#64748b' }}>Seva Plan:</span>
                    <span style={{ color: '#0f2d5c', fontWeight: 800 }}>{selectedPlan.name} ({familyMembers.length} {familyMembers.length === 1 ? 'Devotee' : 'Members'})</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#64748b', fontWeight: 700 }}>Total Payable Dakshina:</span>
                    <strong style={{ color: '#b8860b', fontSize: 16 }}>₹{selectedPlan.price.toLocaleString('en-IN')}</strong>
                  </div>
                </div>

                {/* Dynamic QR Code & Instructions Split Grid */}
                <div className="homam-qr-split-grid">
                  
                  {/* Left: Temple Official QR Code Frame with Embedded Plan Amount */}
                  <div className="homam-qr-frame-box">
                    <div className="homam-qr-img-wrapper" style={{ padding: 10, background: '#ffffff', border: '2px solid #f5c542', borderRadius: 16 }}>
                      <img
                        src={dynamicQrDataUrl || templeQrImg}
                        alt="Sri Gnana Sai Baba Mandir - UPI QR Code"
                        className="homam-qr-code-img"
                        style={{ width: 210, height: 210, objectFit: 'contain', borderRadius: 8, display: 'block' }}
                        onError={(e) => {
                          e.target.src = templeQrImg;
                        }}
                      />
                    </div>
                    <span className="homam-qr-scan-tag">
                      <Sparkles className="w-3 h-3" />
                      <span>₹{selectedPlan.price.toLocaleString('en-IN')} Auto-filled on Scan</span>
                    </span>
                  </div>

                  {/* Right: Payment Details & Guidance */}
                  <div className="homam-qr-details-col">
                    
                    {/* Payee Info */}
                    <div className="homam-qr-payee-info">
                      <div className="homam-qr-payee-label">Temple Payee</div>
                      <div className="homam-qr-payee-name">{templePayee}</div>

                      {/* UPI ID Row */}
                      <div className="homam-upi-copy-row">
                        <span className="homam-upi-id-text">{templeUpiId}</span>
                        <button
                          type="button"
                          onClick={handleCopyUpi}
                          className={`homam-copy-upi-btn ${copiedUpi ? 'copied' : ''}`}
                        >
                          {copiedUpi ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          <span>{copiedUpi ? 'Copied' : 'Copy UPI'}</span>
                        </button>
                      </div>
                    </div>

                    {/* Amount Banner */}
                    <div className="homam-qr-amount-banner">
                      <div>
                        <div className="amt-label">Seva Dakshina Amount</div>
                        <div className="amt-val">₹{selectedPlan.price.toLocaleString('en-IN')}</div>
                      </div>
                      <div className="amt-badge">Fixed in QR</div>
                    </div>

                    {/* Quick Pay via UPI App (for mobile devices) */}
                    <a
                      href={upiPaymentUri}
                      className="btn-open-upi-app"
                      title="Tap to pay directly with Google Pay, PhonePe, or Paytm"
                    >
                      <Smartphone className="w-4 h-4" />
                      <span>Pay via UPI App (GPay / PhonePe / Paytm)</span>
                    </a>

                    {/* 4-Step Instructions */}
                    <ol className="homam-qr-guide-steps">
                      <li>Open <strong>GPay</strong>, <strong>PhonePe</strong>, <strong>Paytm</strong>, or any UPI app.</li>
                      <li>Scan the QR code above (amount <strong>₹{selectedPlan.price.toLocaleString('en-IN')}</strong> will auto-fill).</li>
                      <li>Complete the payment in your UPI app.</li>
                      <li>Copy the <strong>12-digit UPI Reference / UTR Number</strong> from the payment receipt.</li>
                    </ol>
                  </div>
                </div>

                {/* ================= UTR NUMBER INPUT CARD WITH STRICT VALIDATION ================= */}
                <div className={`homam-utr-input-card ${utrError ? 'has-error' : ''}`}>
                  <div className="homam-utr-header">
                    <label htmlFor="homam-utr-input" className="homam-utr-label">
                      <ShieldCheck className="w-4 h-4 text-amber-600" />
                      <span>12-Digit Transaction ID / UTR Number *</span>
                    </label>
                    <span className={`homam-utr-counter ${isUtrComplete ? 'valid' : ''}`}>
                      {utrNumber.length} / 12 digits {isUtrComplete ? '✓' : ''}
                    </span>
                  </div>

                  <p className="homam-utr-subtext">
                    Enter the 12-digit UTR (or UPI Transaction Reference Number) shown on your payment receipt.
                  </p>

                  <div className="homam-utr-field-wrap">
                    <input
                      type="text"
                      id="homam-utr-input"
                      name="utrNumber"
                      placeholder="e.g. 423891028492"
                      value={utrNumber}
                      onChange={handleUtrInputChange}
                      maxLength={22}
                      className="homam-utr-field-input"
                      autoComplete="off"
                      required
                    />

                    <div className="homam-utr-status-icon">
                      {isUtrComplete ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : utrNumber.length > 0 ? (
                        <AlertCircle className="w-5 h-5 text-amber-500" />
                      ) : null}
                    </div>
                  </div>

                  {utrError && (
                    <div className="homam-utr-error-banner">
                      <AlertCircle className="w-4 h-4" />
                      <span>{utrError}</span>
                    </div>
                  )}
                </div>

                {errorMessage && (
                  <div style={{ marginBottom: 16, padding: 12, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AlertCircle className="w-4 h-4" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="homam-flow-actions">
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => setBookingStep(selectedPlan.id === 'individual' ? 'plan_selection' : 'family_members')}
                    className="btn-homam-secondary"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>{selectedPlan.id === 'individual' ? 'Change Plan' : 'Edit Family Members'}</span>
                  </button>

                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={handleUtrPaymentSubmit}
                    className="btn-homam-primary"
                    style={{ flex: 1.5, justifyContent: 'center' }}
                  >
                    {isSubmitting ? (
                      <span>Verifying & Submitting Registration...</span>
                    ) : (
                      <>
                        <Lock className="w-4 h-4" />
                        <span>Submit Sacred Homam Registration</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ================= STEP 4: REGISTRATION RECEIVED & PENDING VERIFICATION ================= */}
          {bookingStep === 'confirmed' && (
            <div style={{ maxWidth: 640, margin: '0 auto', animation: 'fadeInHomam 0.35s ease-out' }}>
              <div className="homam-success-receipt">
                <div className="homam-success-icon">
                  <CheckCircle2 className="w-10 h-10" />
                </div>

                <div className="homam-booking-id-tag">
                  REGISTRATION TOKEN: {bookingRecord?.bookingId || bookingCode}
                </div>

                <h3 style={{ fontSize: '1.85rem', fontWeight: 900, color: '#0f2d5c', margin: '0 0 8px 0' }}>
                  Sacred Homam Registration Received!
                </h3>

                <div style={{ background: '#fef3c7', color: '#92400e', border: '1.5px solid #fde68a', padding: '8px 20px', borderRadius: 24, fontSize: 13, fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: 6, margin: '0 auto 16px auto' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#d97706', display: 'inline-block' }} />
                  <span>Payment Submitted • Pending Mandir Admin Verification</span>
                </div>

                <p style={{ fontSize: 13.5, color: '#475569', maxWidth: 540, margin: '0 auto 18px auto', lineHeight: 1.6 }}>
                  Om Sai Ram! We have successfully received your registration for <strong>{selectedPlan.name}</strong> along with your submitted UTR: <strong style={{ color: '#0f2d5c' }}>{bookingRecord?.utrNumber || utrNumber}</strong>.
                </p>

                {/* Important Notice Regarding Admin Verification & Confirmation Message */}
                <div style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 14, padding: '14px 18px', margin: '0 auto 24px auto', maxWidth: 540, fontSize: 13, color: '#1e3a8a', lineHeight: 1.6, textAlign: 'left', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ fontSize: 20, lineHeight: 1 }}>🛡️</span>
                  <div>
                    <strong style={{ color: '#1e40af', display: 'block', marginBottom: 3 }}>
                      Mandir Admin Verification in Progress
                    </strong>
                    Our Mandir administration will verify your UTR against temple bank records. <strong>Once verified by Admin</strong>, your official confirmation message with Vedic homam timings, dress code guidelines, and sacred sankalpam details will be delivered to your WhatsApp (<strong>+91 {bookingRecord?.mobile}</strong>) and Email.
                  </div>
                </div>

                {/* Full Details Summary Card */}
                <div style={{ background: '#FAF7F2', borderRadius: 16, border: '1px solid #EADDC7', padding: 22, margin: '0 auto 26px auto', textAlign: 'left', fontSize: 13, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 6 }}>
                    <span style={{ color: '#64748b' }}>Primary Devotee:</span>
                    <strong style={{ color: '#0f2d5c' }}>{bookingRecord?.name}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 6 }}>
                    <span style={{ color: '#64748b' }}>Contact Mobile:</span>
                    <strong style={{ color: '#0f2d5c' }}>+91 {bookingRecord?.mobile}</strong>
                  </div>
                  {bookingRecord?.email && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 6 }}>
                      <span style={{ color: '#64748b' }}>Email Address:</span>
                      <strong style={{ color: '#0f2d5c' }}>{bookingRecord?.email}</strong>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 6 }}>
                    <span style={{ color: '#64748b' }}>Honored Nakshatra:</span>
                    <strong style={{ color: '#946608' }}>Day {selectedNakshatra.orderIndex} • {selectedNakshatra.nameEn} ({selectedNakshatra.nameTa})</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 6 }}>
                    <span style={{ color: '#64748b' }}>Homam Date:</span>
                    <strong style={{ color: '#0f2d5c' }}>{selectedNakshatra.assignedDate}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 6 }}>
                    <span style={{ color: '#64748b' }}>Selected Seva Plan:</span>
                    <strong style={{ color: '#0f2d5c' }}>{selectedPlan.name}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 6 }}>
                    <span style={{ color: '#64748b' }}>Seva Dakshina Offering:</span>
                    <strong style={{ color: '#b8860b', fontSize: 16 }}>₹{selectedPlan.price.toLocaleString('en-IN')}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 6, background: '#fef3c7', padding: '8px 10px', borderRadius: 8 }}>
                    <span style={{ color: '#92400e', fontWeight: 700 }}>Submitted UTR Number:</span>
                    <strong style={{ fontFamily: 'monospace', color: '#0f2d5c', fontSize: 14 }}>
                      {bookingRecord?.utrNumber || utrNumber}
                    </strong>
                  </div>

                  {/* Family Members Roster in Receipt */}
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #EADDC7' }}>
                    <span style={{ fontSize: 11.5, color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>
                      Registered Devotees ({familyMembers.length}):
                    </span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                      {familyMembers.map((m, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: '#0f2d5c' }}>
                          <span>{i + 1}. {m.name || 'Devotee'} <em style={{ color: '#64748b', fontSize: 11.5 }}>({m.relationship})</em></span>
                          <span style={{ fontWeight: 600, color: '#92400e' }}>Star: {m.nakshatra || selectedNakshatra.nameEn}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="homam-flow-actions" style={{ flexDirection: 'column', gap: 10 }}>
                  <Link
                    to={getHomamPath('/')}
                    className="btn-homam-primary"
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span>View All 27 Nakshatra Homam Schedule</span>
                  </Link>

                  <Link
                    to="/"
                    className="btn-homam-secondary"
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    <span>Back to Sri Gnana Sai Baba Mandir Home</span>
                  </Link>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>

      <HomamFooter />
    </div>
  );
}

export default HomamBookingPage;
